import { useState, type FC } from "react";
import IconButton from "../../atoms/buttons/IconButton";
import { theme } from "../../../theme";
import { TrashIcon } from "../../atoms/icons/TrashIcon";
import CircleButton from "../../atoms/buttons/CircleButton";
import Modal from "../Modal";
import Subtitle from "../../atoms/typography/Subtitle";
import { useTranslation } from "react-i18next";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Body from "../../atoms/typography/Body";
import styled from "styled-components";
import DefaultButton from "../../atoms/buttons/DefaultButton";
import ActionButton from "../../atoms/buttons/ActionButton";
import Alert from "../Alert";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import TextButton from "../../atoms/buttons/TextButton";

interface Props {
  deleteButtonType:
    | "iconButton"
    | "circleButton"
    | "outlinedButtonSmall"
    | "outlinedButtonMedium"
    | "iconButtonMedium"
    | "textButton";
  onClickDelete: () => void;
  title?: string;
  message?: string;
  onCompletedDelte?: () => void;
  displaySuccessMessage?: boolean;
  loading?: boolean;
  errorMesssage?: string;
  disabled?: boolean;
}

const ActionButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 32px;
  align-items: center;
`;

const DeleteModal: FC<Props> = ({
  deleteButtonType,
  onClickDelete,
  title,
  message,
  onCompletedDelte,
  loading,
  errorMesssage,
  disabled,
}) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);

  const onDelete = () => {
    onClickDelete();
    if (onCompletedDelte) {
      onCompletedDelte();
    } else if (errorMesssage) {
      return;
    } else {
      setModalOpen(false);
    }
  };

  return (
    <div>
      {deleteButtonType === "iconButton" && (
        <IconButton
          color={theme.colors.error20}
          icon={<TrashIcon color={theme.colors.error} />}
          onClick={() => setModalOpen(true)}
          disabled={disabled}
          loading={loading}
        />
      )}
      {deleteButtonType === "iconButtonMedium" && (
        <IconButton
          color={theme.colors.error20}
          icon={<TrashIcon color={theme.colors.error} />}
          onClick={() => setModalOpen(true)}
          size="medium"
          disabled={disabled}
          loading={loading}
        />
      )}
      {deleteButtonType === "circleButton" && (
        <CircleButton
          color={theme.colors.error}
          icon={<TrashIcon color={theme.colors.error} />}
          onClick={() => setModalOpen(true)}
          disabled={disabled}
          loading={loading}
        />
      )}
      {deleteButtonType === "outlinedButtonSmall" && (
        <OutlinedButton
          title={title ?? t("common.delete")}
          color={theme.colors.error}
          icon={<TrashIcon color={theme.colors.error} />}
          onClick={() => setModalOpen(true)}
          disabled={disabled}
          loading={loading}
        />
      )}
      {deleteButtonType === "outlinedButtonMedium" && (
        <OutlinedButton
          title={title ?? t("common.delete")}
          color={theme.colors.error}
          icon={<TrashIcon color={theme.colors.error} />}
          onClick={() => setModalOpen(true)}
          medium
          disabled={disabled}
          loading={loading}
        />
      )}
      {deleteButtonType === "textButton" && (
        <TextButton
          title={title ?? t("common.delete")}
          color={theme.colors.error}
          icon={<TrashIcon color={theme.colors.error} />}
          onClick={() => setModalOpen(true)}
          disabled={disabled}
          loading={loading}
        />
      )}
      {modalOpen && (
        <Modal closeIcon onClose={() => setModalOpen(false)} width={400}>
          <Subtitle color={theme.colors.primary} textalign="center">
            {title ?? t("common.delete")}
          </Subtitle>
          <HorizontalSpacer spacing={2} />
          <Body textalign="center">
            {message ?? t("random.areYouSureYouWantToDelete")}
          </Body>
          <HorizontalSpacer spacing={2} />

          <ActionButtonsContainer>
            <DefaultButton
              title={t("common.cancel")}
              onClick={() => setModalOpen(false)}
            />
            <ActionButton
              title={t("common.delete")}
              onClick={onDelete}
              color={theme.colors.error}
              icon={<TrashIcon color={theme.colors.white} />}
              loading={loading}
            />
          </ActionButtonsContainer>
          {errorMesssage && (
            <div>
              <HorizontalSpacer spacing={1} />
              <Alert type="error" title={errorMesssage} />
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

export default DeleteModal;
